import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { getExercise, submitQuiz } from '../lib/api';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, ArrowRight, BookOpen, Volume2, VolumeX, AlertTriangle, Eye } from 'lucide-react';

// Sound Effects Hook
const useSoundEffects = () => {
  const audioContextRef = useRef(null);
  const [soundEnabled, setSoundEnabled] = useState(true);

  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  const playTone = useCallback((frequency, duration, type = 'sine', volume = 0.1) => {
    if (!soundEnabled) return;
    try {
      const ctx = initAudio();
      if (ctx.state === 'suspended') ctx.resume();
      
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = type;
      
      gainNode.gain.setValueAtTime(0, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + duration);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration);
    } catch (e) { /* Audio may not be available */ }
  }, [soundEnabled, initAudio]);

  const playSelect = useCallback(() => {
    playTone(600, 0.08, 'sine', 0.08);
  }, [playTone]);

  const playSuccess = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = initAudio();
      if (ctx.state === 'suspended') ctx.resume();
      
      const notes = [523.25, 659.25, 783.99, 1046.50];
      notes.forEach((freq, i) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        oscillator.frequency.value = freq;
        oscillator.type = 'sine';
        
        const startTime = ctx.currentTime + (i * 0.1);
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.15, startTime + 0.02);
        gainNode.gain.linearRampToValueAtTime(0, startTime + 0.3);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + 0.3);
      });
    } catch (e) { /* Audio may not be available */ }
  }, [soundEnabled, initAudio]);

  return { soundEnabled, setSoundEnabled, playSelect, playSuccess };
};

const QuizPage = () => {
  const navigate = useNavigate();
  const { exerciseId, sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const isReadingMode = searchParams.get('reading') === 'true';
  
  const [exercise, setExercise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [passageOpen, setPassageOpen] = useState(isReadingMode); // Start expanded for reading mode

  const { soundEnabled, setSoundEnabled, playSelect, playSuccess } = useSoundEffects();

  // Tab-switch anti-cheat detection
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [autoSubmitting, setAutoSubmitting] = useState(false);
  const tabSwitchRef = useRef(0); // Use ref to track in event handlers

  // Play warning sound
  const playWarning = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.frequency.value = 200;
      oscillator.type = 'square';
      gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.3);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.3);
    } catch (e) { /* Audio may not be available */ }
  }, [soundEnabled]);

  // Handle tab/window visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && !autoSubmitting) {
        // User left the page
        const newCount = tabSwitchRef.current + 1;
        tabSwitchRef.current = newCount;
        setTabSwitchCount(newCount);
        
        if (newCount === 1) {
          setWarningMessage('Warning: You left the quiz page. Please stay on this page to avoid having your quiz auto-submitted.');
          setShowWarningModal(true);
          playWarning();
        } else if (newCount === 2) {
          setWarningMessage('Final Warning: If you leave this page again, your quiz will be automatically submitted with your current answers.');
          setShowWarningModal(true);
          playWarning();
        } else if (newCount >= 3) {
          // Auto-submit on 3rd violation
          setWarningMessage('Quiz auto-submitted due to leaving the page multiple times.');
          setShowWarningModal(true);
          setAutoSubmitting(true);
        }
      }
    };

    const handleBlur = () => {
      // Also detect window blur (clicking outside browser)
      if (!document.hidden && !autoSubmitting) {
        // Small delay to avoid false positives from clicking UI elements
        setTimeout(() => {
          if (!document.hasFocus()) {
            handleVisibilityChange();
          }
        }, 100);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
    };
  }, [autoSubmitting, playWarning]);

  // Auto-submit when triggered by tab switch violations
  useEffect(() => {
    if (autoSubmitting && exercise && !submitting) {
      const autoSubmit = async () => {
        setSubmitting(true);
        try {
          // Fill in null answers with empty if needed
          const finalAnswers = answers.map((a, idx) => 
            a || { question_index: idx, answer: null }
          );
          
          const res = await submitQuiz(exerciseId, sessionId, finalAnswers, tabSwitchRef.current);
          
          if (res.data && res.data.quiz_score !== undefined) {
            toast.warning(`Quiz auto-submitted due to tab switching. Score: ${res.data.quiz_score}%`);
            await new Promise(resolve => setTimeout(resolve, 500));
            navigate(`/results/${exerciseId}/${sessionId}`);
          }
        } catch (error) {
          toast.error('Failed to auto-submit quiz');
          setAutoSubmitting(false);
          setShowWarningModal(false);
        } finally {
          setSubmitting(false);
        }
      };
      autoSubmit();
    }
  }, [autoSubmitting, exercise, submitting, answers, exerciseId, sessionId, navigate]);

  useEffect(() => {
    const fetchExercise = async () => {
      try {
        const res = await getExercise(exerciseId);
        setExercise(res.data);
        setAnswers(new Array(res.data.quiz_questions?.length || 5).fill(null));
      } catch (error) {
        console.error('Failed to load quiz:', error);
        // Retry once after a delay
        try {
          await new Promise(resolve => setTimeout(resolve, 1000));
          const res = await getExercise(exerciseId);
          setExercise(res.data);
          setAnswers(new Array(res.data.quiz_questions?.length || 5).fill(null));
        } catch (retryError) {
          console.error('Retry also failed:', retryError);
          toast.error('Failed to load quiz. Please go back and try again.');
          navigate('/student');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchExercise();
  }, [exerciseId, navigate]);

  const handleAnswer = (answer) => {
    playSelect();
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = { question_index: currentQuestion, answer };
    setAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentQuestion < (exercise?.quiz_questions?.length || 0) - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    const unanswered = answers.filter(a => a === null).length;
    if (unanswered > 0) {
      toast.error(`Please answer all questions (${unanswered} remaining)`);
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await submitQuiz(exerciseId, sessionId, answers, tabSwitchRef.current);
      
      // Verify the quiz score was actually saved
      if (res.data && res.data.quiz_score !== undefined) {
        playSuccess();
        if (tabSwitchRef.current > 0) {
          toast.success(`Quiz complete! Score: ${res.data.quiz_score}% (${tabSwitchRef.current} tab switch${tabSwitchRef.current > 1 ? 'es' : ''} recorded)`);
        } else {
          toast.success(`Quiz complete! Score: ${res.data.quiz_score}%`);
        }
        // Add small delay before navigation to ensure DB write completes
        await new Promise(resolve => setTimeout(resolve, 300));
        navigate(`/results/${exerciseId}/${sessionId}`);
      } else {
        toast.error('Quiz submission incomplete - please try again');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const questions = exercise?.quiz_questions || [];
  const question = questions[currentQuestion];
  const selectedAnswer = answers[currentQuestion]?.answer;
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-background">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-background-paper z-50">
        <div 
          className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-6 text-center relative">
          {/* Sound Toggle */}
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={`absolute right-0 top-0 p-2 rounded-full transition-colors ${soundEnabled ? 'text-neon-green hover:bg-neon-green/20' : 'text-text-muted hover:bg-white/10'}`}
            title={soundEnabled ? 'Sound On' : 'Sound Off'}
            data-testid="sound-toggle"
          >
            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
          
          <p className="text-primary font-mono text-sm mb-2">
            Question {currentQuestion + 1} of {questions.length}
          </p>
          <h1 className="font-heading text-2xl font-bold">Comprehension Quiz</h1>
          <p className="text-text-secondary mt-2">{exercise?.title}</p>
        </div>

        {/* Passage Reference - Collapsible (always expanded in reading mode) */}
        <Collapsible open={passageOpen} onOpenChange={setPassageOpen} className="mb-6">
          <Card className="card-glass overflow-hidden">
            <CollapsibleTrigger asChild>
              <button 
                className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                data-testid="toggle-passage-btn"
              >
                <div className="flex items-center gap-3">
                  <BookOpen className="w-5 h-5 text-primary" />
                  <span className="font-medium">
                    {isReadingMode || exercise?.exercise_type === 'reading' 
                      ? 'Read the Passage' 
                      : 'View Passage Reference'}
                  </span>
                  {(isReadingMode || exercise?.exercise_type === 'reading') && (
                    <span className="text-xs bg-secondary/20 text-secondary px-2 py-0.5 rounded-full">
                      Reading Quiz
                    </span>
                  )}
                </div>
                {passageOpen ? (
                  <ChevronUp className="w-5 h-5 text-text-muted" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-muted" />
                )}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-4 pb-4 border-t border-white/10">
                <div className="relative mt-4">
                  {/* Clean, readable passage text */}
                  <div 
                    className={`mt-4 p-4 bg-black/20 rounded-lg overflow-y-auto ${
                      isReadingMode || exercise?.exercise_type === 'reading' 
                        ? 'max-h-96' 
                        : 'max-h-64'
                    }`}
                  >
                    <p className="font-mono text-base leading-relaxed text-text-secondary whitespace-pre-wrap">
                      {exercise?.passage}
                    </p>
                  </div>
                </div>
                {(isReadingMode || exercise?.exercise_type === 'reading') && (
                  <p className="text-xs text-text-muted mt-3 text-center">
                    Read the passage carefully, then answer the questions below
                  </p>
                )}
              </div>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Question Card */}
        <Card className="card-glass p-8 mb-6" data-testid="quiz-question-card">
          {/* Question text */}
          <p className="text-lg mb-6">{question?.question}</p>
          
          {/* Answer options */}
          <div className="space-y-3">
            {question?.options?.map((option, idx) => {
              const optionLetter = option.charAt(0);
              const isSelected = selectedAnswer === optionLetter;
              
              return (
                <button
                  key={idx}
                  className={`w-full text-left p-4 rounded-lg border transition-all ${
                    isSelected 
                      ? 'border-primary bg-primary/10 neon-border' 
                      : 'border-white/10 hover:border-white/30 hover:bg-white/5'
                  }`}
                  onClick={() => handleAnswer(optionLetter)}
                  data-testid={`quiz-option-${optionLetter}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                      isSelected ? 'bg-primary text-black' : 'bg-white/10'
                    }`}>
                      {optionLetter}
                    </span>
                    <span className="flex-1">{option.substring(3)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Question Dots */}
        <div className="flex justify-center gap-2 mb-8">
          {questions.map((_, idx) => (
            <button
              key={idx}
              className={`w-3 h-3 rounded-full transition-all ${
                idx === currentQuestion 
                  ? 'bg-primary scale-125' 
                  : answers[idx] 
                    ? 'bg-status-success' 
                    : 'bg-white/20'
              }`}
              onClick={() => setCurrentQuestion(idx)}
              data-testid={`question-dot-${idx}`}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            className="btn-secondary rounded-sm"
            onClick={handlePrev}
            disabled={currentQuestion === 0}
            data-testid="prev-question-btn"
          >
            Previous
          </Button>
          
          {currentQuestion === questions.length - 1 ? (
            <Button
              className="btn-primary rounded-sm px-8"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="submit-quiz-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Quiz'}
            </Button>
          ) : (
            <Button
              className="btn-primary rounded-sm"
              onClick={handleNext}
              disabled={!selectedAnswer}
              data-testid="next-question-btn"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </main>

      {/* Tab Switch Warning Indicator */}
      {tabSwitchCount > 0 && !showWarningModal && (
        <div className="fixed bottom-4 left-4 bg-status-warning/20 border border-status-warning/50 rounded-lg px-4 py-2 flex items-center gap-2 z-40">
          <Eye className="w-4 h-4 text-status-warning" />
          <span className="text-sm text-status-warning">
            Tab switches: {tabSwitchCount}/3
          </span>
        </div>
      )}

      {/* Warning Modal */}
      {showWarningModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="card-glass w-full max-w-md p-6 animate-in zoom-in-95 duration-200 border-status-warning/50">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-status-warning/20 flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-status-warning" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-2 text-status-warning">
                {autoSubmitting ? 'Quiz Auto-Submitted' : `Warning ${tabSwitchCount}/3`}
              </h3>
              <p className="text-text-secondary mb-6">
                {warningMessage}
              </p>
              {!autoSubmitting && (
                <Button
                  className="btn-primary w-full"
                  onClick={() => setShowWarningModal(false)}
                  data-testid="dismiss-warning-btn"
                >
                  I Understand - Continue Quiz
                </Button>
              )}
              {autoSubmitting && (
                <div className="flex items-center justify-center gap-2 text-text-muted">
                  <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <span>Submitting your answers...</span>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default QuizPage;
